import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for polling analysis status until completion
 * 
 * @param {boolean} isActive - Whether polling should be active
 * @param {Function} checkStatusFn - Function to check current status
 * @param {Function} onStatusChange - Callback when status changes
 * @param {number} intervalMs - Polling interval in milliseconds (default: 15000)
 */
function useAnalysisPolling(isActive, checkStatusFn, onStatusChange, intervalMs = 15000) {
  const intervalRef = useRef(null);
  const isPollingRef = useRef(false);
  
  const startPolling = useCallback(async () => {
    if (isPollingRef.current || !isActive) return;
    
    isPollingRef.current = true;
    
    const poll = async () => {
      try {
        const result = await checkStatusFn();
        onStatusChange(result);
        
        // Check if we should continue polling
        const hasPendingAnalysis = checkForPendingAnalysis(result);
        
        if (!hasPendingAnalysis || !isActive) {
          stopPolling();
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on error, but log it
      }
    };
    
    // Start immediate check
    await poll();
    
    // Set up interval if still needed
    if (isPollingRef.current && isActive) {
      intervalRef.current = setInterval(poll, intervalMs);
    }
  }, [isActive, checkStatusFn, onStatusChange, intervalMs]);
  
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);
  
  // Helper function to check if there are any pending analyses
  const checkForPendingAnalysis = (data) => {
    if (!data) return false;
    
    // Check for pending JD analysis
    if (data.jd_analysis_status === 'PENDING') {
      return true;
    }
    
    // Check for pending CV analyses in candidates
    if (data.candidates && Array.isArray(data.candidates)) {
      return data.candidates.some(candidate => 
        candidate.cv_analysis_status === 'PENDING'
      );
    }
    
    // Check if data is a single candidate
    if (data.cv_analysis_status === 'PENDING') {
      return true;
    }
    
    return false;
  };
  
  // Start/stop polling based on isActive
  useEffect(() => {
    if (isActive) {
      startPolling();
    } else {
      stopPolling();
    }
    
    return stopPolling;
  }, [isActive, startPolling, stopPolling]);
  
  // Cleanup on unmount
  useEffect(() => {
    return stopPolling;
  }, [stopPolling]);
  
  return {
    startPolling,
    stopPolling,
    isPolling: isPollingRef.current
  };
}

/**
 * Hook specifically for polling need analysis status
 */
export function useNeedAnalysisPolling(needId, isActive, refreshNeedFn, onStatusChange) {
  const checkStatusFn = useCallback(async () => {
    if (!needId) return null;
    return await refreshNeedFn(needId);
  }, [needId, refreshNeedFn]);
  
  return useAnalysisPolling(isActive, checkStatusFn, onStatusChange);
}

